import { Scissors } from "lucide-react";
import { asepsisExtraCases } from "./asepsisCases";

// Surgical Asepsis & Instrument Prep — Cert IV: prepare for surgery / sterilisation.
export const asepsisConfig = {
  simId: "asepsis",
  simName: "VetSim Theatre",
  subtitle: "Surgical asepsis & instrument prep",
  title: "Surgical Asepsis & Instrument Prep",
  accent: "#0d9488",
  Icon: Scissors,
  topic: "asepsis",
  steps: ["Scrub, gown & glove", "Spot the breach", "Instruments", "Pack & sterilise", "Verify sterility"],
  introBlurb:
    "Set up theatre and protect the sterile field. Work through the surgical scrub, gown and closed-glove sequence, catch contamination breaches on the sterile-field diagram, identify and pass the right instrument, run the autoclave correctly, and verify sterility before the patient arrives. Graded against a Cert IV asepsis rubric.",
  cases: [
    ...asepsisExtraCases,
    {
      id: "routine-desex",
      name: "Theatre setup — routine bitch spay",
      meta: "Scrub nurse role",
      briefing:
        "You're scrubbing in to assist a routine ovariohysterectomy. Set up and maintain the sterile field.",
      beats: [
        { type: "order", key: "s1", label: "Scrub, gown & glove",
          title: "The scrub sequence", prompt: "Tap the steps in the correct aseptic order.",
          items: [
            { id: "scrub", text: "Surgical hand scrub (timed antiseptic)" },
            { id: "dry", text: "Dry hands with a sterile towel" },
            { id: "gown", text: "Don the sterile gown (touch the inside only)" },
            { id: "glove", text: "Closed-glove (hands stay inside the cuffs)" },
          ], full: 20 },
        { type: "sterilefield", key: "s2", label: "Spot the breach",
          title: "Spot the contamination", prompt: "Tap every action on the diagram that breaks sterility.",
          spots: [
            { t: "Reaching across the field, ungloved", flag: true, top: "46%", left: "52%", coach: "Anything non-sterile passing over the field contaminates it." },
            { t: "Gloved hands dropped below the waist", flag: true, top: "82%", left: "17%", coach: "Below the waist (or out of sight) is considered non-sterile." },
            { t: "Touching mask/cap after gloving", flag: true, top: "26%", left: "15%", coach: "Mask/cap are non-sterile — touching them contaminates the gloves." },
            { t: "Back turned to the sterile table", flag: true, top: "58%", left: "33%", coach: "Your back is non-sterile; never turn it to the field." },
            { t: "Hands clasped, in sight above the waist", flag: false, top: "44%", left: "16%", coach: "" },
            { t: "Instruments kept over the draped field", flag: false, top: "34%", left: "82%", coach: "" },
          ], full: 20 },
        { type: "instruments", key: "s3", label: "Instruments",
          title: "Instrument identification", prompt: "The surgeon asks for the instrument to clamp and ligate the ovarian pedicle. Tap the right one.",
          options: [
            { id: "haemostat", name: "Artery forceps", icon: "haemostat", correct: true, note: "Haemostatic (artery) forceps clamp the vessel/pedicle before it's ligated." },
            { id: "needleHolder", name: "Needle holders", icon: "needleHolder", correct: false, note: "Needle holders drive suture needles — they don't clamp a pedicle." },
            { id: "towelClamp", name: "Towel clamps", icon: "towelClamp", correct: false, note: "Towel clamps secure drapes." },
            { id: "scissors", name: "Mayo scissors", icon: "scissors", correct: false, note: "Scissors cut tissue/suture." },
            { id: "scalpel", name: "Scalpel handle", icon: "scalpel", correct: false, note: "The scalpel makes the incision." },
            { id: "forceps", name: "Thumb forceps", icon: "forceps", correct: false, note: "Thumb forceps grasp/steady tissue." },
          ], full: 20 },
        { type: "autoclave", key: "s4", label: "Pack & sterilise",
          title: "Pack & autoclave", prompt: "Run the steam steriliser, then tap the correct sterilisation practices.",
          target: { temp: 121, pressure: 15, time: 15 },
          chips: [
            { t: "Place a chemical indicator strip inside the pack", flag: true, coach: "Internal indicators confirm the sterilant reached the pack centre." },
            { t: "Use autoclave (indicator) tape outside and date the pack", flag: true, coach: "External tape shows it was processed; dating gives a shelf-life." },
            { t: "Don't overload the chamber; allow steam to circulate", flag: true, coach: "Overpacking blocks steam penetration and causes sterilisation failure." },
            { t: "Run the correct time/temp/pressure cycle (121°C)", flag: true, coach: "Sterility depends on the validated cycle parameters." },
            { t: "Wrap packs tightly sealed so no steam gets in", flag: false, coach: "" },
            { t: "Re-use packs past their expiry to save stock", flag: false, coach: "" },
          ], full: 20 },
        { type: "choice", key: "s5", label: "Verify sterility", last: true,
          title: "Verify before use", prompt: "You pick up a pack for the next surgery. What makes it unsafe to use?",
          options: [
            { t: "The wrap is torn/damp, the indicator hasn't changed colour, or it's past its expiry date — any of these means it's not safe and must be re-sterilised.", score: 3, note: "Integrity, indicator change and in-date are all required before opening a pack." },
            { t: "As long as it's clearly one of our own packs off the sterile shelf, you can just open it and get on — the checks are really only needed for brand-new stock.", score: 0, note: "Always verify wrap integrity, the indicator and the date first." },
            { t: "If it only looks very slightly damp from the cycle that's completely normal, so go ahead and use it rather than holding up the next surgery.", score: 0, note: "A damp pack has lost sterility (wicking) — never use it." },
          ], full: 20 },
      ],
    },
  ],
};
